import { Flex } from '@mantine/core';
import { useEffect } from 'react';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import { Client } from 'src/users';
import { Family } from 'src/clients/hooks/useFamily';
import { useGenerateDataExport } from 'src/data-export/hooks/useGenerateDataExport';
import { useDataExportConfigOptions } from 'src/data-export/hooks/useDataExportConfigOptions';
import { SettingsRow } from 'src/suitability-report/SettingsRow';

type DataExportData = {
  client?: Client;
  family?: Family;
  selectionType?: 'client' | 'family';
  includeSensitiveFields?: boolean;
  configId?: string;
};

export default function GenerateOptionsStep(props: StepProps<DataExportData>) {
  const { data, setData } = props;
  const { generateDataExport, isLoading } = useGenerateDataExport();
  const { configs } = useDataExportConfigOptions();

  useEffect(() => {
    if (data.configId) return;
    const defaultConfig = configs.find(c => c.isDefault);
    if (!defaultConfig) return;
    setData(prev => ({ ...prev, configId: defaultConfig.id }));
  }, [configs, data.configId, setData]);

  const handleToggle = (value: boolean) => {
    setData({ ...data, includeSensitiveFields: value });
  };

  const handleGenerate = async () => {
    if (!data.configId) {
      notifyAndLogError(
        'Error',
        'No configuration available. Please try again.',
        new Error('Missing config'),
      );
      return;
    }

    try {
      if (data.selectionType === 'client' && data.client) {
        await generateDataExport('client', data.client.id, data.client.id, {
          config_id: data.configId,
          include_sensitive_fields: Boolean(data.includeSensitiveFields),
        });
      } else if (data.selectionType === 'family' && data.family) {
        await generateDataExport('family', data.family.id, data.family.mainFamilyMember.clientId, {
          config_id: data.configId,
          include_sensitive_fields: Boolean(data.includeSensitiveFields),
        });
      } else {
        notifyAndLogError(
          'Error',
          'Failed to generate data export. A client or family must be selected.',
          new Error('No client or family selected'),
        );
      }

      props.onSuccess();
    } catch (error) {
      notifyAndLogError('Error', 'Failed to generate data export', error);
    }
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Generate"
      description="Configure settings and generate data export"
      continueText={isLoading ? 'Generating data export' : 'Generate Data Export'}
      canGoForward={!isLoading && !!data.configId}
      canGoBack={!isLoading}
      nextStep={handleGenerate}
      isSubmitting={isLoading}
    >
      <Flex direction="column" gap="md">
        <SettingsList>
          <SettingsRow
            title="Show sensitive fields"
            description="Whether to include sensitive fields such as addiction history, mental health considerations, and caring responsibilities in the data export."
            checked={Boolean(props.data.includeSensitiveFields)}
            onChange={handleToggle}
          />
        </SettingsList>
      </Flex>
    </DrawerFlowScreenLayout>
  );
}

type SettingsListProps = {
  children: React.ReactNode;
};

function SettingsList({ children }: SettingsListProps) {
  return (
    <Flex direction="column" bg="gray.0" style={{ borderRadius: 12 }}>
      <Flex
        direction="column"
        gap="xs"
        p="md"
        bd="1px solid var(--mantine-color-gray-1)"
        style={{ borderRadius: 12 }}
        bg="white"
      >
        {children}
      </Flex>
    </Flex>
  );
}
